import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import Escudo from '../components/Escudo';
import { 
  FaUser,
  FaUserTie,
  FaChurch,
  FaHandsHelping,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaArrowLeft,
  FaQrcode,
  FaCheckCircle,
  FaPrint
} from 'react-icons/fa';
import { getFileUrl } from '../utils/fileUtils';
import './ProfileDetail.css';

const ProfileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [qrUrl, setQrUrl] = useState('');

  // Decodificar el ID para obtener tipo e id
  const decodeProfileId = (encodedId) => {
    try {
      // El formato es: tipo_perfil-id
      const parts = encodedId.split('-');
      if (parts.length >= 2) {
        const tipo = parts[0];
        const profileId = parts.slice(1).join('-');
        return { tipo, id: profileId };
      }
      return null;
    } catch (error) {
      console.error('Error decodificando ID:', error);
      return null;
    }
  };

  const profileInfo = decodeProfileId(id);

  // Obtener perfil
  const { data: perfil, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['profile-detail', id],
    queryFn: async () => {
      if (!profileInfo) {
        throw new Error('ID de perfil inválido');
      }
      const response = await api.get(`/search-profile/${profileInfo.tipo}/${profileInfo.id}`);
      return response.data;
    },
    enabled: !!profileInfo,
    retry: 1,
    staleTime: 60 * 1000
  });

  useEffect(() => {
    if (id) {
      const origin = window.location.origin;
      setQrUrl(`${origin}/qr/${id}`);
    }
  }, [id]);

  const handlePrint = () => {
    window.print();
  };

  const formatFecha = (valor) => {
    if (!valor) return '';
    const d = new Date(valor);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    return String(valor);
  };

  const qrImageSrc = qrUrl
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=${encodeURIComponent(qrUrl)}`
    : '';

  const getTipoIcon = (tipo) => {
    const icons = {
      autoridad: FaUserTie,
      misionero: FaChurch,
      parroco: FaChurch,
      voluntario: FaHandsHelping,
      usuario: FaUser
    };
    return icons[tipo] || FaUser;
  };

  const getTipoLabel = (tipo) => {
    const labels = {
      autoridad: 'Autoridad',
      misionero: 'Misionero',
      parroco: 'Párroco',
      voluntario: 'Voluntario',
      usuario: 'Usuario'
    };
    return labels[tipo] || tipo;
  };

  if (!profileInfo) {
    return (
      <div className="profile-detail-page">
        <div className="profile-detail-error">
          <h2>Enlace no válido</h2>
          <p>El identificador del perfil no es correcto.</p>
          <Link to="/search-profile" className="btn-back">Volver a búsqueda</Link>
        </div>
      </div>
    );
  }

  if (isLoading || (isFetching && !perfil)) {
    return (
      <div className="profile-detail-page">
        <div className="profile-detail-loading">
          <p>Cargando perfil...</p>
        </div>
      </div>
    );
  }

  if (error || !perfil) {
    const apiMsg = error?.response?.data?.error || error?.message;
    const sinConexion = error?.code === 'ECONNABORTED' || error?.message?.includes('Network');
    return (
      <div className="profile-detail-page">
        <div className="profile-detail-error">
          <h2>{sinConexion ? 'No se pudo conectar con el servidor' : 'Perfil no encontrado'}</h2>
          <p>
            {sinConexion
              ? 'La API no responde. Compruebe que el servicio Node esté activo y que MySQL esté en ejecución.'
              : apiMsg || 'El perfil que buscas no existe o ha sido eliminado.'}
          </p>
          <button type="button" className="btn-back-profile" onClick={() => refetch()}>
            Reintentar
          </button>
          <Link to="/search-profile" className="btn-back">
            <FaArrowLeft /> Volver a búsqueda
          </Link>
        </div>
      </div>
    );
  }

  const Icon = getTipoIcon(perfil.tipo_perfil);

  // Determinar el título según el tipo de perfil
  const getProfileTitle = () => {
    if (perfil.cargo) return perfil.cargo;
    if (perfil.tipo && perfil.tipo_perfil === 'misionero') return perfil.tipo;
    if (perfil.tipo_perfil === 'parroco') return 'Párroco';
    if (perfil.tipo_perfil === 'voluntario') return 'Voluntario';
    return getTipoLabel(perfil.tipo_perfil);
  };

  // Determinar si está activo
  const isActivo = perfil.activo !== false && perfil.estado !== 'inactivo';

  return (
    <div className="profile-detail-page profile-detail-carnet-page">
      <section className="section">
      <div className="container profile-carnet-container">
        <div className="profile-carnet-toolbar profile-carnet-no-print">
          <button type="button" onClick={handlePrint} className="btn-print-carnet">
            <FaPrint /> Imprimir carnet
          </button>
          <button type="button" onClick={() => navigate(-1)} className="btn-back-profile btn-back-inline">
            <FaArrowLeft /> Volver
          </button>
        </div>

        <div className="profile-carnet-print-area">
          <div className="profile-detail-header-section">
            <div className="profile-detail-header-unified">
              <Escudo />
              <div className="profile-detail-header-siid">
                <h1 className="profile-detail-app-title">SISTEMA INTEGRADO DE INFORMACIÓN DIOCESANA</h1>
                <h2 className="profile-detail-app-acronym">SIID</h2>
              </div>
            </div>
          </div>

          <div className="profile-detail-content">
            <div className="profile-detail-card profile-carnet-body">
            <div className="profile-photo-section">
              {perfil.foto_url || perfil.imagen_url ? (
                <div className="profile-photo-circular">
                  <img 
                    src={getFileUrl(perfil.foto_url || perfil.imagen_url)} 
                    alt={perfil.nombre || perfil.nombre_completo || perfil.parroco}
                    onError={(e) => {
                      e.target.src = `${process.env.PUBLIC_URL || ''}/images/default-avatar.png`;
                    }}
                  />
                  {isActivo && (
                    <div className="profile-photo-checkmark">
                      <FaCheckCircle />
                    </div>
                  )}
                </div>
              ) : (
                <div className="profile-photo-circular default">
                  <Icon />
                  {isActivo && (
                    <div className="profile-photo-checkmark">
                      <FaCheckCircle />
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="profile-name-section">
              <h1 className="profile-name-large">
                {perfil.nombre || perfil.nombre_completo || perfil.parroco || 'Sin nombre'}
              </h1>
              <p className="profile-title-text">{getProfileTitle()}</p>
              {(perfil.nombre_parroquia && perfil.nombre_parroquia !== 'Directorio presbiteral') && (
                <p className="profile-carnet-parroquia">{perfil.nombre_parroquia}</p>
              )}
              {perfil.nombre_parroquia === 'Directorio presbiteral' && (
                <p className="profile-carnet-parroquia">Directorio presbiteral</p>
              )}
              {isActivo && (
                <div className="profile-status-active">ACTIVO</div>
              )}
            </div>

            <div className="profile-carnet-qr" aria-label="Código QR de verificación">
              {qrImageSrc ? (
                <img src={qrImageSrc} alt={`QR verificación ${id}`} className="profile-carnet-qr-img" />
              ) : (
                <div className="profile-carnet-qr-placeholder"><FaQrcode /></div>
              )}
              <p className="profile-carnet-qr-label">Escanear para verificar</p>
              <p className="profile-carnet-qr-id">{id}</p>
            </div>
            </div>
          </div>

          <div className="profile-detail-extra profile-detail-extra--print">
            <div className="profile-details-grid">
              <div className="profile-detail-item">
                <span className="profile-detail-label">Tipo de Documento</span>
                <span className="profile-detail-value">
                  {perfil.tipo_documento || 'Cédula de Ciudadanía'}
                </span>
              </div>
              <div className="profile-detail-item">
                <span className="profile-detail-label">Documento</span>
                <span className="profile-detail-value">
                  {perfil.documento ? `*****${perfil.documento.slice(-3)}` : 'No disponible'}
                </span>
              </div>
              {perfil.fecha_ordenacion && (
                <div className="profile-detail-item">
                  <span className="profile-detail-label">Fecha de Ordenación</span>
                  <span className="profile-detail-value">{formatFecha(perfil.fecha_ordenacion)}</span>
                </div>
              )}
              {perfil.fecha_registro && (
                <div className="profile-detail-item">
                  <span className="profile-detail-label">Fecha de Registro</span>
                  <span className="profile-detail-value">{formatFecha(perfil.fecha_registro)}</span>
                </div>
              )}
            </div>

            {/* Asignación Pastoral */}
            {(perfil.nombre_parroquia || perfil.mision_actual || perfil.asignacion_pastoral) && (
              <div className="profile-pastoral-assignment">
                <h3 className="profile-pastoral-title">Asignación Pastoral Actual</h3>
                <p className="profile-pastoral-value">
                  {perfil.nombre_parroquia || perfil.mision_actual || perfil.asignacion_pastoral || 'Adscrito'}
                </p>
              </div>
            )}

            {/* Información adicional */}
            {(perfil.biografia || perfil.descripcion || perfil.experiencia || perfil.habilidades) && (
              <div className="profile-additional-info">
                {perfil.biografia || perfil.descripcion ? (
                  <div className="profile-info-item">
                    <h4 className="profile-info-title">Biografía</h4>
                    <p className="profile-info-text">{perfil.biografia || perfil.descripcion}</p>
                  </div>
                ) : null}
                
                {perfil.experiencia && (
                  <div className="profile-info-item">
                    <h4 className="profile-info-title">Experiencia</h4>
                    <p className="profile-info-text">{perfil.experiencia}</p>
                  </div>
                )}

                {perfil.habilidades && (
                  <div className="profile-info-item">
                    <h4 className="profile-info-title">Habilidades</h4>
                    <p className="profile-info-text">{perfil.habilidades}</p>
                  </div>
                )}
              </div>
            )}

            {/* Información de contacto */}
            {(perfil.email || perfil.telefono || perfil.direccion || perfil.contacto) && (
              <div className="profile-contact-section">
                <h3 className="profile-contact-title">Información de Contacto</h3>
                <div className="profile-contact-grid">
                  {perfil.email && (
                    <div className="profile-contact-item">
                      <FaEnvelope className="profile-contact-icon" />
                      <div className="profile-contact-content">
                        <span className="profile-contact-label">Email</span>
                        <a href={`mailto:${perfil.email}`} className="profile-contact-value">
                          {perfil.email}
                        </a>
                      </div>
                    </div>
                  )}

                  {perfil.telefono && (
                    <div className="profile-contact-item">
                      <FaPhone className="profile-contact-icon icon-phone-upright" />
                      <div className="profile-contact-content">
                        <span className="profile-contact-label">Teléfono</span>
                        <a href={`tel:${perfil.telefono}`} className="profile-contact-value">
                          {perfil.telefono}
                        </a>
                      </div>
                    </div>
                  )}

                  {perfil.direccion && (
                    <div className="profile-contact-item">
                      <FaMapMarkerAlt className="profile-contact-icon" />
                      <div className="profile-contact-content">
                        <span className="profile-contact-label">Dirección</span>
                        <span className="profile-contact-value">{perfil.direccion}</span>
                      </div>
                    </div>
                  )}

                  {perfil.contacto && (
                    <div className="profile-contact-item">
                      <FaPhone className="profile-contact-icon icon-phone-upright" />
                      <div className="profile-contact-content">
                        <span className="profile-contact-label">Contacto</span>
                        <span className="profile-contact-value">{perfil.contacto}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
      </section>
    </div>
  );
};

export default ProfileDetail;





